import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { Loader2, Check, X } from 'lucide-react';

type ProfessionType = 'professions' | 'craft_professions' | 'hospitality_categories' | 'professionals_categories';

interface Profession {
  id: number;
  name: string;
  is_accepted: boolean;
  category?: string;
  created_at: string;
}

const AdminProfessionApproval: React.FC = () => {
  const [professionType, setProfessionType] = useState<ProfessionType>('professions');
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [error, setError] = useState('');

  const professionTypeLabels = {
    professions: 'Tradesman Professions',
    craft_professions: 'Craftsman Professions',
    hospitality_categories: 'Hospitality Professions',
    professionals_categories: 'Professionals Professions',
  };

  useEffect(() => {
    fetchProfessions();
  }, [professionType]);

  const fetchProfessions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { data, error: fetchError } = await supabase
        .from(professionType)
        .select('*')
        .order('is_accepted')
        .order('name');

      if (fetchError) throw fetchError;
      
      setProfessions(data || []);
    } catch (err) {
      console.error('Error fetching professions:', err);
      setError('Failed to load professions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number, currentStatus: boolean) => {
    try {
      setSaving(prev => ({ ...prev, [id]: true }));
      
      const { error } = await supabase
        .from(professionType)
        .update({ is_accepted: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      setProfessions(prev => 
        prev.map(prof => 
          prof.id === id ? { ...prof, is_accepted: !currentStatus } : prof
        )
      );
    } catch (err) {
      console.error('Error updating profession:', err);
      setError('Failed to update profession status');
    } finally {
      setSaving(prev => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-bold mb-6">Profession Approvals</h2>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Profession Type
        </label>
        <div className="flex space-x-4">
          {Object.entries(professionTypeLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setProfessionType(key as ProfessionType)}
              className={`px-4 py-2 rounded-md ${
                professionType === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                {(professionType === 'hospitality_categories' || professionType === 'professionals_categories') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {professions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No professions found
                  </td>
                </tr>
              ) : (
                professions.map((profession) => (
                  <tr key={`${professionType}-${profession.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {profession.name}
                      </div>
                    </td>
                    {(professionType === 'hospitality_categories' || professionType === 'professionals_categories') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {profession.category || 'N/A'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        profession.is_accepted 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {profession.is_accepted ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleApprove(profession.id, profession.is_accepted)}
                        disabled={saving[profession.id]}
                        className={`mr-2 ${
                          profession.is_accepted 
                            ? 'text-yellow-600 hover:text-yellow-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                      >
                        {saving[profession.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : profession.is_accepted ? (
                          <X className="w-5 h-5" />
                        ) : (
                          <Check className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminProfessionApproval;
